/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

interface AdminForceLogoutProps {
  userIsAdmin: boolean;
}

export default function AdminForceLogout({ userIsAdmin }: AdminForceLogoutProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [lastForceLogout, setLastForceLogout] = useState<Date | null>(null);
  const [triggeredBy, setTriggeredBy] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadForceLogoutStatus();
  }, []);

  const loadForceLogoutStatus = async () => {
    try {
      const db = getFirestore();
      const configRef = doc(db, 'system_config', 'force_logout');
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const data = configDoc.data();
        if (data.lastForceLogoutTime) {
          setLastForceLogout(data.lastForceLogoutTime.toDate());
          setTriggeredBy(data.triggeredBy || null);
        }
      }
    } catch (err) {
      console.error('Error loading force logout status:', err);
    }
  };

  const handleForceLogout = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const idToken = await user.getIdToken();
      
      // Call Netlify function to set force logout timestamp
      const response = await fetch(`${API_BASE_URL}/.netlify/functions/admin-force-logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: reason.trim() || 'Security update'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to trigger force logout');
      }

      setSuccess('Force logout triggered successfully! Users will be logged out on their next page load.');
      setShowConfirm(false);
      setReason('');
      await loadForceLogoutStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (!userIsAdmin) {
    return null;
  }

  return (
    <div>
      <h3>Force User Re-Login</h3>
      <p >
        Force all users to re-authenticate on their next page load. Useful after security updates or major changes.
      </p>

      {/* Current Status */}
      <div>
        <div>
          <div>
            <span>Last Force Logout:</span>
            <span className={lastForceLogout ? 'text-gray-900' : 'text-gray-500'}>
              {formatTimestamp(lastForceLogout)}
            </span>
          </div>
          {triggeredBy && (
            <div>
              <span>Triggered By:</span>
              <span className="text-gray-900">{triggeredBy}</span>
            </div>
          )}
        </div>
      </div>

      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          style={{'marginTop':'20px'}}
        >
          🔒 Trigger Force Logout
        </button>
      ) : (
        <div className="space-y-3">
          <div className="">
            <p>⚠️ Confirm Force Logout</p>
            <p>
              This will force ALL users (who logged in before now) to re-authenticate on their next page load.
            </p>
          </div>

          <div>
            <label>
              Reason (optional):
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Security update, major changes"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={isLoading}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleForceLogout}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Yes, Force Logout'}
            </button>
            <button
              onClick={() => {
                setShowConfirm(false);
                setReason('');
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {success && (
        <div>
          <p>✅ Success</p>
          <p>{success}</p>
        </div>
      )}

      {error && (
        <div>
          <p>❌ Error</p>
          <p>{error}</p>
        </div>
      )}
    </div>
  );
}